import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'
import Joi from 'joi'
import toast from 'react-hot-toast'

import P from '../../../components/typography/P'

import { getFromLocalStorage, parseJSON } from '../../../utils/util'

import {
  setId,
  setFirstName,
  setMiddleName,
  setProfessionalSummary,
  setLastName,
  setCvImageLink,
  setContactNumber,
  setCountry,
  setCity,
  setAddress,
  setZipCode,
  setBasis,
  setMinSalary,
  setMaxSalary,
  setCurrencies,
} from '../../../redux/feature/applicantSlice'

import { StandardInput, PhoneNumberInput } from '../../../components/Input'
import { SingleSelect } from '../../../components/Select'

import SingleCountrySelect from '../../../components/CustomSelect/SingleCountrySelect'
import AsyncSingleCitySelect from '../../../components/CustomSelect/AsyncSingleCitySelect'
import SingleCurrencySelect from '../../../components/CustomSelect/SingleCurrencySelect'

import CVImageUpload from './CVImageUpload'
import PhrasedRichTextBox from './PhrasedRichTextBox'

import avatar from '../../../assets/images/avatar-temp.png'

const getSelectionValue = (option, fallback = '') => {
  if (option === null || option === undefined) return fallback

  if (typeof option === 'object') {
    return option.value ?? fallback
  }

  return option
}

const PersonalInformation = forwardRef((props, ref) => {
  const dispatch = useDispatch()

  const {
    firstName,
    middleName,
    lastName,
    professionalSummary,
    cvImageLink,
    contactNumber,
    country,
    city,
    address,
    zipCode,
    basis,
    minSalary,
    maxSalary,
    currencies,
  } = useSelector((state) => state.applicant)

  const validationSchema = useMemo(() => {
    return Joi.object({
      firstName: Joi.string().label('First Name').required(),
      middleName: Joi.string().label('Middle Name').required(),
      lastName: Joi.string().label('Last Name').required(),
      professionalSummary: Joi.string()
        .label('Professional Summary')
        .required()
        .custom((value, helpers) => {
          // Remove HTML tags and trim whitespace
          const cleanedValue = value.replace(/<[^>]*>/g, '').trim()
          if (!cleanedValue) {
            return helpers.error('string.empty')
          }
          return value
        }),
      contactNumber: Joi.string().label('Contact Number').required(),
      country: Joi.object({
        value: Joi.string().required(),
        label: Joi.string().required(),
      })
        .label('Country')
        .required()
        .messages({
          'object.base': 'Country must be a valid selection.',
          'any.required': 'Country is required.',
        }),
      city: Joi.object({
        value: Joi.string().required(),
        label: Joi.string().required(),
      })
        .label('City')
        .required()
        .messages({
          'object.base': 'City must be a valid selection.',
          'any.required': 'City is required.',
        }),
      address: Joi.string().label('Address').required(),
      zipCode: Joi.string().label('Zip Code').required(),
      minSalary: Joi.number()
        .integer()
        .positive()
        .less(Joi.ref('maxSalary'))
        .label('Min Salary')
        .required()
        .messages({
          'number.base': 'Min Salary must be a valid number.',
          'number.less': 'Min Salary must be less than Max Salary.',
          'number.positive': 'Min Salary must be a positive number.',
          'any.required': 'Min Salary is required.',
        }),

      maxSalary: Joi.number()
        .integer()
        .positive()
        .label('Max Salary')
        .required()
        .messages({
          'number.base': 'Max Salary must be a valid number.',
          'number.positive': 'Max Salary must be a positive number.',
          'any.required': 'Max Salary is required.',
        }),
      basis: Joi.object({
        value: Joi.string().required(),
        label: Joi.string().required(),
      })
        .label('Salary Basis')
        .required()
        .messages({
          'object.base': 'Salary Basis must be a valid selection.',
          'any.required': 'Salary Basis is required.',
        }),
      currencies: Joi.object({
        value: Joi.string().required(),
        label: Joi.string().required(),
      })
        .label('Currency')
        .required()
        .messages({
          'object.base': 'Currency must be a valid selection.',
          'any.required': 'Currency is required.',
        }),
    })
  }, [])

  const [fieldErrors, setFieldErrors] = useState({})

  const applicantCVData = useMemo(
    () => getFromLocalStorage('applicant-cv-data'),
    []
  )
  const applicantSalaryBasis = useMemo(
    () => ({
      value: applicantCVData?.salary_basis || 'Per Month',
      label: applicantCVData?.salary_basis || 'Per Month',
    }),
    [applicantCVData]
  )
  const applicantData = useMemo(() => {
    const data = getFromLocalStorage('applicant-data')

    if (!data || typeof data !== 'object') return data

    return {
      ...data,
      city:
        typeof data.city === 'object' && data.city !== null
          ? data.city.value
          : data.city,
      country:
        typeof data.country === 'object' && data.country !== null
          ? data.country.value
          : data.country,
    }
  }, [])

  const salaryBasisList = useMemo(
    () => [
      {
        value: 'Per Month',
        label: 'Per Month',
      },
      {
        value: 'Per Year',
        label: 'Per Year',
      },
    ],
    []
  )

  const defaultProfessionalSummaryPhrases = [
    'Dedicated Customer Service Representative committed to providing quality care for the ultimate customer satisfaction.',
    'Experienced and effective recruiter adept in all aspects of recruitment, support, and solutions.',
    'Bringing forth expertise in web design with a track record of leading design projects with high levels of client satisfaction.',
    'Results-driven Project Manager with a proven ability to lead cross-functional teams and deliver projects on time and within budget.',
    'Detail-oriented Administrative Professional skilled in optimizing workflows, supporting daily operations, and improving organizational efficiency.',
    'Innovative Software Developer experienced in building scalable applications and delivering clean, maintainable code.',
    'Highly organized Marketing Specialist with strong skills in campaign planning, content development, and brand management.',
    'Analytical Data Analyst skilled in transforming complex datasets into actionable insights to support strategic decision-making.',
    'Resourceful Operations Coordinator experienced in streamlining processes, managing logistics, and enhancing productivity across departments.',
    'Motivated Sales Professional with a strong background in lead generation, client relationship management, and achieving revenue targets.',
  ]

  const handleSelectCountry = (e) => {
    dispatch(setCountry({ label: e.label, value: e.value }))
  }

  const handleSelectCity = (e) => dispatch(setCity(e))
  const handleSelectSalaryBasis = (e) => dispatch(setBasis(e))
  const handleSelectCurrency = (e) => dispatch(setCurrencies(e))

  /**
   * Validates form data, displays validation errors if any,
   * and returns true if validation passes.
   *
   * @returns {boolean} Whether the form data is valid.
   */
  const handleNext = () => {
    // Construct form data from current state values
    const formData = {
      firstName,
      middleName,
      lastName,
      professionalSummary,
      country,
      city,
      address,
      zipCode,
      minSalary,
      maxSalary,
      basis,
      currencies,
      contactNumber,
    }

    // Validate form data against schema, collecting all errors
    const result = validationSchema.validate(formData, { abortEarly: false })

    // If there are validation errors, set error messages for fields and notify the user
    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)
      toast.error('Please fix the validation errors to continue.')
      return false
    }

    // normalize the country, city, and currency values
    const normalizedCountry = getSelectionValue(country)
    const normalizedCity = getSelectionValue(city)
    const normalizedCurrency = getSelectionValue(currencies, 'PHP')

    const meta = parseJSON(applicantCVData?.meta, {})

    const updatedMeta = {
      ...meta,
      streetAddress: address,
      ZIPCode: zipCode,
    }

    const updatedApplicantCVData = {
      ...(applicantCVData || {}),
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      cv_image_url: cvImageLink,
      professional_summary: professionalSummary,
      currencies: normalizedCurrency,
      meta: JSON.stringify(updatedMeta),
    }

    const updatedApplicantData = {
      ...(applicantData || {}),
      phone_number: contactNumber,
      city: normalizedCity,
      country: normalizedCountry,
    }

    // save to local storage...
    localStorage.setItem('applicant-data', JSON.stringify(updatedApplicantData))
    localStorage.setItem(
      'applicant-cv-data',
      JSON.stringify(updatedApplicantCVData)
    )

    return true
  }

  const handleImageChange = (link) => dispatch(setCvImageLink(link))

  useEffect(() => {
    if (applicantCVData) {
      dispatch(setId(applicantCVData?.applicant_id || ''))
      dispatch(setFirstName(applicantCVData?.first_name || ''))
      dispatch(setMiddleName(applicantCVData?.middle_name || ''))
      dispatch(setLastName(applicantCVData?.last_name || ''))
      dispatch(setCvImageLink(applicantCVData?.cv_image_url || avatar))
      dispatch(
        setProfessionalSummary(applicantCVData?.professional_summary || '')
      )
      dispatch(
        setCurrencies({
          value: applicantCVData?.currencies || 'PHP',
          label: applicantCVData?.currencies || 'PHP',
        })
      )

      dispatch(setBasis(applicantSalaryBasis))
      dispatch(setMinSalary(applicantCVData.min_salary))
      dispatch(setMaxSalary(applicantCVData.max_salary))

      const meta = parseJSON(applicantCVData?.meta, {})
      dispatch(setAddress(meta?.streetAddress || ''))
      dispatch(setZipCode(meta?.ZIPCode || ''))
    }

    if (applicantData) {
      dispatch(setContactNumber(applicantData.phone_number))

      const applicantCountry = {
        value: applicantData?.country,
        label: applicantData?.country,
      }

      const applicantCity = {
        value: applicantData?.city,
        label: applicantData?.city,
      }

      dispatch(setCountry(applicantCountry))
      dispatch(setCity(applicantCity))
      dispatch(setContactNumber(applicantData.phone_number))
    }
  }, [])

  useImperativeHandle(ref, () => ({
    onNext: handleNext,
  }))

  return (
    <StyledPersonalInformation>
      <P size="1.5rem" weight="500" mb="0.25rem">
        Personal Information
      </P>
      <P color="#6F6F6F" mb="1rem">
        Share a few details so recruiters always see your most up-to-date info.
      </P>
      <FormGroup>
        <InputGroup>
          <StandardInput
            title="First Name"
            type="text"
            value={firstName}
            onChange={(value) => dispatch(setFirstName(value))}
            required
            wrapperClass={fieldErrors.firstName && 'error'}
            subText={fieldErrors.firstName}
            marginBottom="0.5rem"
          />
          <StandardInput
            title="Middle Name"
            type="text"
            value={middleName}
            onChange={(value) => dispatch(setMiddleName(value))}
            required
            wrapperClass={fieldErrors.middleName && 'error'}
            subText={fieldErrors.middleName}
            marginBottom="0.5rem"
          />
          <StandardInput
            title="Last Name"
            type="text"
            value={lastName}
            onChange={(value) => dispatch(setLastName(value))}
            required
            wrapperClass={fieldErrors.lastName && 'error'}
            subText={fieldErrors.lastName}
            marginBottom="0.5rem"
          />
        </InputGroup>
        <CVImageUpload
          onChangeImage={handleImageChange}
          imageLink={cvImageLink}
        />
      </FormGroup>

      <PhrasedRichTextBox
        value={professionalSummary}
        onChange={(value) => dispatch(setProfessionalSummary(value))}
        title="Professional Summary"
        required
        className={fieldErrors.professionalSummary && 'has-error'}
        errorMessage={fieldErrors.professionalSummary}
        subHeading="2-3 sentences that describe your professional experience"
        phrases={defaultProfessionalSummaryPhrases}
      />

      <SingleCountrySelect
        country={country}
        onSelectCountry={handleSelectCountry}
        validationError={fieldErrors.country}
      />

      <AsyncSingleCitySelect
        city={city}
        onSelectCity={handleSelectCity}
        country={country.value}
        validationError={fieldErrors.city}
      />

      <StandardInput
        title="Address"
        type="text"
        value={address}
        onChange={(value) => dispatch(setAddress(value))}
        required
        wrapperClass={fieldErrors.address && 'error'}
        subText={fieldErrors.address}
        marginBottom="0.5rem"
      />
      <StandardInput
        title="Zip Code"
        type="text"
        value={zipCode}
        onChange={(value) => dispatch(setZipCode(value))}
        required
        wrapperClass={fieldErrors.zipCode && 'error'}
        subText={fieldErrors.zipCode}
        marginBottom="0.5rem"
      />
      <FormGroup>
        <StandardInput
          title="Min. Salary"
          type="number"
          value={minSalary}
          onChange={(value) => dispatch(setMinSalary(value))}
          required
          wrapperClass={fieldErrors.minSalary && 'error'}
          subText={fieldErrors.minSalary}
          marginBottom="0.5rem"
        />
        <StandardInput
          title="Max. Salary"
          type="number"
          value={maxSalary}
          onChange={(value) => dispatch(setMaxSalary(value))}
          required
          wrapperClass={fieldErrors.maxSalary && 'error'}
          subText={fieldErrors.maxSalary}
          marginBottom="0.5rem"
        />
      </FormGroup>
      <FormGroup>
        <SingleSelect
          title="Salary Basis"
          options={salaryBasisList}
          selected={basis}
          placeholder="Select Salary Basis"
          onChange={handleSelectSalaryBasis}
          wrapperClass={fieldErrors.basis && 'error'}
          subText={fieldErrors.basis}
        />
        <SingleCurrencySelect
          currency={currencies}
          onSelectCurrency={handleSelectCurrency}
          validationError={fieldErrors.currencies}
        />
      </FormGroup>
      <PhoneNumberInput
        title="Phone Number"
        country="ph"
        value={contactNumber}
        required
        wrapperClass={fieldErrors.contactNumber && 'error'}
        onChange={(value) => dispatch(setContactNumber(value))}
        subText={fieldErrors.contactNumber}
      />
    </StyledPersonalInformation>
  )
})

const StyledPersonalInformation = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 1rem;
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`

PersonalInformation.displayName = 'PersonalInformation'

export default PersonalInformation
